import { useCallback, useEffect, useState } from "react";
import { EnvelopeSimple, FilePdf, Printer, DownloadSimple, PencilSimple } from "@phosphor-icons/react";
import { MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { API, BACKEND_URL, formatCurrency, fmtErr, today, PAYMENT_MODES, isUpgradeError, toastUpgrade } from "../lib/api";
import StatusBadge from "./StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";

export function InvoiceDetail({ doc, onClose, onChanged }) {
  const [payments, setPayments] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [amount, setAmount] = useState("");
  const [transactionNo, setTransactionNo] = useState("");
  const [paymentExchangeRate, setPaymentExchangeRate] = useState("1");
  const [mode, setMode] = useState("Cash");
  const [accountId, setAccountId] = useState("");
  const [payDate, setPayDate] = useState(today());
  const [saving, setSaving] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [invoiceEmailEnabled, setInvoiceEmailEnabled] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);
  const [pdfTemplate, setPdfTemplate] = useState("classic");
  const [editForm, setEditForm] = useState({
    party_address: "", party_district: "", party_state: "", party_pin_code: "",
    ship_to_different: false, shipping_address: "", shipping_district: "",
    shipping_state: "", shipping_pin_code: "", eway_bill_no: "", vehicle_no: "",
    terms_and_conditions: "",
  });
  const isMoneyDoc = doc && (doc.type === "sale" || doc.type === "purchase");
  const documentId = doc?.id;
  const documentType = doc?.type;

  const load = useCallback(async () => {
    if (!doc || !isMoneyDoc) return;
    try {
      const [p, a] = await Promise.all([API.get(`/payments?doc_id=${doc.id}`), API.get("/banking/accounts")]);
      setPayments(p.data);
      setAccounts(a.data);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }, [doc, isMoneyDoc]);

  useEffect(() => {
    setAmount("");
    setTransactionNo("");
    setPaymentExchangeRate("1");
    setAccountId("");
    setPdfTemplate("classic");
    load();
  }, [load]);

  useEffect(() => {
    let active = true;
    if (!documentId || !["sale", "estimate", "credit_note"].includes(documentType)) {
      setInvoiceEmailEnabled(null);
      return () => { active = false; };
    }
    setInvoiceEmailEnabled(null);
    API.get("/billing/my-subscription")
      .then(({ data }) => {
        if (active) setInvoiceEmailEnabled(data.plan?.features?.invoice_email ?? true);
      })
      .catch((e) => {
        if (active) {
          setInvoiceEmailEnabled(false);
          toast.error(fmtErr(e));
        }
      });
    return () => { active = false; };
  }, [documentId, documentType]);

  if (!doc) return null;
  const isSale = doc.type === "sale";
  const PARTY_NAMES = { sale: "Customer", purchase: "Vendor", estimate: "Customer", po: "Vendor", credit_note: "Customer", debit_note: "Vendor" };
  const due = Math.max(0, Math.round((doc.total - (doc.tds_amount || 0) - doc.paid_amount) * 100) / 100);
  const currency = doc.currency || "INR";
  const exchangeRate = Number(doc.exchange_rate) > 0 ? Number(doc.exchange_rate) : 1;
  const docMoney = (value) => formatCurrency(Number(value || 0) / exchangeRate, currency);
  const pdfUrl = `${BACKEND_URL}/api/documents/${doc.id}/pdf?template=${pdfTemplate}`;
  const billToLines = [
    doc.party_address,
    [doc.party_district, doc.party_state, doc.party_pin_code].filter(Boolean).join(", "),
    doc.party_phone,
    doc.party_email,
    doc.party_gstin && `GSTIN: ${doc.party_gstin}`,
  ].filter(Boolean);
  const shipToLines = [
    doc.shipping_address,
    [doc.shipping_district, doc.shipping_state, doc.shipping_pin_code].filter(Boolean).join(", "),
  ].filter(Boolean);

  const printDoc = () => {
    const frame = document.createElement("iframe");
    frame.style.display = "none";
    frame.src = `${BACKEND_URL}/api/documents/${doc.id}/pdf?template=${pdfTemplate}`;
    document.body.appendChild(frame);
    frame.onload = () => {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
    };
  };

  const emailDoc = async () => {
    setEmailing(true);
    try {
      const r = await API.post(`/documents/${doc.id}/email`);
      toast.success(`Emailed to ${r.data.emailed_to}`);
    } catch (e) {
      if (isUpgradeError(e)) toastUpgrade(e);
      else toast.error(fmtErr(e));
    } finally {
      setEmailing(false);
    }
  };

  const shareDocument = (channel) => {
    const labels = {
      sale: "sales invoice",
      purchase: "purchase bill",
      estimate: "estimate",
      po: "purchase order",
      credit_note: "credit note",
      debit_note: "debit note",
    };
    const documentLabel = labels[doc.type] || "document";
    const message = `Hello ${doc.party_name || "there"}, please find the ${documentLabel} ${doc.number} dated ${doc.date} for ${docMoney(doc.total)}. I will attach the PDF.`;
    const encodedMessage = encodeURIComponent(message);
    const url = channel === "whatsapp"
      ? `https://wa.me/?text=${encodedMessage}`
      : `https://t.me/share/url?text=${encodedMessage}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openEdit = async () => {
    try {
      const response = await API.get(`/invoices/${doc.id}`);
      const invoice = response.data;
      setEditForm({
        party_address: invoice.party_address || "",
        party_district: invoice.party_district || "",
        party_state: invoice.party_state || "",
        party_pin_code: invoice.party_pin_code || "",
        ship_to_different: !!(invoice.ship_to_different && invoice.shipping_address),
        shipping_address: invoice.shipping_address || "",
        shipping_district: invoice.shipping_district || "",
        shipping_state: invoice.shipping_state || "",
        shipping_pin_code: invoice.shipping_pin_code || "",
        eway_bill_no: invoice.eway_bill_no || "",
        vehicle_no: invoice.vehicle_no || "",
        terms_and_conditions: invoice.terms_and_conditions || "",
      });
      setEditOpen(true);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const saveInvoiceDetails = async () => {
    if (editForm.ship_to_different && !editForm.shipping_address.trim()) {
      return toast.error("Enter the shipping address");
    }
    if (editForm.party_pin_code && !/^\d{6}$/.test(editForm.party_pin_code)) {
      return toast.error("Billing PIN code must be 6 digits");
    }
    if (editForm.ship_to_different && editForm.shipping_pin_code && !/^\d{6}$/.test(editForm.shipping_pin_code)) {
      return toast.error("Shipping PIN code must be 6 digits");
    }
    setSavingDetails(true);
    try {
      await API.patch(`/invoices/${doc.id}/details`, editForm);
      toast.success("Invoice details updated");
      setEditOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSavingDetails(false);
    }
  };

  const recordPayment = async () => {
    if (!amount || Number(amount) <= 0) return toast.error("Enter a valid amount");
    if (currency !== "INR" && (!Number.isFinite(Number(paymentExchangeRate)) || Number(paymentExchangeRate) <= 0)) return toast.error("Enter a valid INR exchange rate");
    setSaving(true);
    try {
      await API.post("/payments", {
        doc_id: doc.id,
        doc_type: doc.type,
        amount: Number(amount),
        currency,
        exchange_rate: currency === "INR" ? 1 : Number(paymentExchangeRate),
        transaction_no: transactionNo.trim(),
        date: payDate,
        account_id: accountId || null,
        mode,
      });
      toast.success("Payment recorded");
      setAmount("");
      setTransactionNo("");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!window.confirm(`Delete ${doc.number}? Stock and linked payments will be reversed.`)) return;
    try {
      await API.delete(`/invoices/${doc.id}`);
      toast.success(`${doc.number} deleted`);
      onClose();
      onChanged?.();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <>
    <Dialog open={!!doc} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="invoice-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 font-heading tracking-tight">
            {doc.number} <StatusBadge status={doc.status} />
          </DialogTitle>
          <DialogDescription className="sr-only">Invoice details, payments and totals</DialogDescription>
        </DialogHeader>
        <div className="grid gap-2 text-sm sm:grid-cols-3">
          <div><span className="text-muted-foreground">{PARTY_NAMES[doc.type]}: </span><span className="font-medium" data-testid="invoice-detail-party">{doc.party_name}</span></div>
          <div><span className="text-muted-foreground">Date: </span><span data-testid="invoice-detail-date">{doc.date}</span></div>
          <div><span className="text-muted-foreground">POS: </span><span>{doc.place_of_supply}</span></div>
        </div>
        {billToLines.length > 0 && (
          <div className={`grid gap-4 text-sm ${doc.ship_to_different && shipToLines.length ? "sm:grid-cols-2" : "sm:grid-cols-1"}`} data-testid="invoice-detail-addresses">
            <section className="rounded-md border p-3" data-testid="invoice-detail-bill-to">
              <h3 className="mb-1 font-semibold">Bill To · {doc.party_name}</h3>
              {billToLines.map((line, index) => <p key={index} className="text-muted-foreground">{line}</p>)}
            </section>
            {doc.ship_to_different && shipToLines.length > 0 && (
              <section className="rounded-md border p-3" data-testid="invoice-detail-ship-to">
                <h3 className="mb-1 font-semibold">Ship To</h3>
                {shipToLines.map((line, index) => <p key={index} className="text-muted-foreground">{line}</p>)}
              </section>
            )}
          </div>
        )}
        {(doc.eway_bill_no || doc.vehicle_no || doc.terms_and_conditions) && (
          <section className="space-y-1 rounded-md border p-3 text-sm" data-testid="invoice-detail-transport-terms">
            {doc.eway_bill_no && <p><span className="font-medium">E-Way Bill No.:</span> {doc.eway_bill_no}</p>}
            {doc.vehicle_no && <p><span className="font-medium">Vehicle No.:</span> {doc.vehicle_no}</p>}
            {doc.terms_and_conditions && (
              <div>
                <p className="font-medium">Terms and Conditions</p>
                <p className="whitespace-pre-wrap text-muted-foreground">{doc.terms_and_conditions}</p>
              </div>
            )}
          </section>
        )}
        {doc.ref_number && (
          <p className="text-xs text-muted-foreground" data-testid="note-ref-line">
            Against {doc.ref_number}{doc.reason ? ` · ${doc.reason}` : ""}
          </p>
        )}
        {doc.converted_number && (
          <p className="rounded-md border border-[#008A52]/30 bg-[#008A52]/10 px-3 py-2 text-xs text-[#008A52]" data-testid="converted-note">
            Converted to {doc.converted_number}
          </p>
        )}

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead>HSN</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead className="text-right">GST %</TableHead>
                <TableHead className="text-right">Taxable</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {doc.items.map((l, i) => (
                <TableRow key={i}>
                  <TableCell className="font-medium">
                    {l.name}
                    {[l.batch_no && `Batch: ${l.batch_no}`, l.mfg_date && `Mfg: ${l.mfg_date}`, l.exp_date && `Exp: ${l.exp_date}`,
                      l.boxes ? `Boxes: ${l.boxes}` : "", l.mrp ? `MRP: ${docMoney(l.mrp)}` : "", l.retail_price ? `Retail: ${docMoney(l.retail_price)}` : ""]
                      .filter(Boolean).length > 0 && (
                      <p className="text-xs font-normal text-muted-foreground" data-testid={`invoice-detail-line-extra-${i}`}>
                        {[l.batch_no && `Batch: ${l.batch_no}`, l.mfg_date && `Mfg: ${l.mfg_date}`, l.exp_date && `Exp: ${l.exp_date}`,
                          l.boxes ? `Boxes: ${l.boxes}` : "", l.mrp ? `MRP: ${docMoney(l.mrp)}` : "", l.retail_price ? `Retail: ${docMoney(l.retail_price)}` : ""]
                          .filter(Boolean).join(" · ")}
                      </p>
                    )}
                  </TableCell>
                  <TableCell>{l.hsn || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{l.qty}</TableCell>
                  <TableCell className="text-right tabular-nums">{docMoney(l.rate)}</TableCell>
                  <TableCell className="text-right tabular-nums">{l.gst_rate}%</TableCell>
                  <TableCell className="text-right tabular-nums">{docMoney(l.taxable)}</TableCell>
                  <TableCell className="text-right tabular-nums">{docMoney(l.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {isMoneyDoc ? (
          <div className="space-y-2">
            <p className="text-sm font-semibold">Payments</p>
            {payments.length === 0 && <p className="text-xs text-muted-foreground">No payments recorded yet.</p>}
            {payments.map((p) => (
              <div key={p.id} className="flex justify-between rounded border px-3 py-2 text-sm" data-testid={`payment-row-${p.id}`}>
                <span>{p.date} · {p.mode}{p.transaction_no ? ` · ${p.transaction_no}` : ""}</span>
                <span className="font-medium tabular-nums">{formatCurrency(Number(p.currency_amount) > 0 ? p.currency_amount : p.amount, p.currency || "INR")}</span>
              </div>
            ))}
            {due > 0 && (
              <div className="space-y-2 rounded-md border border-dashed p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Record Payment</p>
                <div className="grid grid-cols-2 gap-2">
                  <Input type="number" min="0" step="any" placeholder={`Due ${docMoney(due)}`} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="payment-amount-input" />
                  <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} data-testid="payment-date-input" />
                  {currency !== "INR" && (
                    <div className="space-y-1">
                      <Label className="text-xs">Payment rate (1 {currency} in INR)</Label>
                      <Input type="number" min="0" step="any" value={paymentExchangeRate} onChange={(e) => setPaymentExchangeRate(e.target.value)} data-testid="payment-exchange-rate-input" />
                    </div>
                  )}
                  <Select value={mode} onValueChange={setMode}>
                    <SelectTrigger data-testid="payment-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAYMENT_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={accountId} onValueChange={setAccountId}>
                    <SelectTrigger data-testid="payment-account-select"><SelectValue placeholder="Account (optional)" /></SelectTrigger>
                    <SelectContent>
                      {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="col-span-2 space-y-1">
                    <Label className="text-xs">Transaction No. / Narration (optional)</Label>
                    <Input maxLength={255} value={transactionNo} onChange={(e) => setTransactionNo(e.target.value)} data-testid="payment-transaction-no-input" />
                  </div>
                </div>
                <Button size="sm" onClick={recordPayment} disabled={saving} data-testid="record-payment-button">
                  {saving ? "Saving…" : "Record"}
                </Button>
              </div>
            )}
          </div>
          ) : (
          <div className="space-y-2">
            <p className="text-sm font-semibold">Notes</p>
            <p className="text-sm text-muted-foreground">{doc.notes || "—"}</p>
          </div>
          )}
          <div className="space-y-1.5 rounded-md border bg-muted/40 p-4 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Taxable</span><span className="tabular-nums">{docMoney(doc.taxable)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">CGST</span><span className="tabular-nums">{docMoney(doc.cgst)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">SGST</span><span className="tabular-nums">{docMoney(doc.sgst)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">IGST</span><span className="tabular-nums">{docMoney(doc.igst)}</span></div>
            <div className="flex justify-between border-t pt-2 font-heading text-base font-bold"><span>Total</span><span className="tabular-nums" data-testid="invoice-detail-total">{docMoney(doc.total)}</span></div>
            {isMoneyDoc && doc.tds_amount > 0 && (
              <>
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Less: TDS @ {doc.tds_rate}%</span><span className="tabular-nums text-[#E02424]" data-testid="invoice-detail-tds">− {docMoney(doc.tds_amount)}</span></div>
                <div className="flex justify-between text-sm font-semibold"><span>Net Payable</span><span className="tabular-nums" data-testid="invoice-detail-net">{docMoney(doc.total - doc.tds_amount)}</span></div>
              </>
            )}
            {isMoneyDoc && (
              <>
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Paid</span><span className="tabular-nums text-[#008A52]">{docMoney(doc.paid_amount)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Due</span><span className="tabular-nums text-[#E02424]" data-testid="invoice-detail-due">{docMoney(due)}</span></div>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <Label htmlFor="document-pdf-template" className="text-sm text-muted-foreground">PDF template</Label>
              <Select value={pdfTemplate} onValueChange={setPdfTemplate}>
                <SelectTrigger id="document-pdf-template" className="w-[190px]" data-testid="document-pdf-template-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="classic">Classic</SelectItem>
                  <SelectItem value="modern">Modern</SelectItem>
                  {doc.type === "sale" && <SelectItem value="proforma">Proforma Invoice</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap gap-2">
            {["sale", "estimate"].includes(doc.type) && (
              <Button variant="outline" size="sm" onClick={openEdit} data-testid="invoice-edit-details-button">
                <PencilSimple size={15} className="mr-1" /> {doc.type === "estimate" ? "Edit Estimate" : "Edit Details"}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => window.open(pdfUrl, "_blank")} data-testid="invoice-pdf-button">
              <FilePdf size={15} className="mr-1" /> PDF
            </Button>
            <Button variant="outline" size="sm" onClick={printDoc} data-testid="invoice-print-button">
              <Printer size={15} className="mr-1" /> Print
            </Button>
            <Button variant="outline" size="sm" asChild data-testid="invoice-download-button">
              <a href={pdfUrl} download={`${doc.number}${pdfTemplate === "proforma" ? "-proforma" : ""}.pdf`}>
                <DownloadSimple size={15} className="mr-1" /> Download
              </a>
            </Button>
            <Button variant="outline" size="sm" onClick={() => shareDocument("whatsapp")} data-testid="invoice-share-whatsapp-button">
              <MessageCircle size={15} className="mr-1" /> WhatsApp
            </Button>
            <Button variant="outline" size="sm" onClick={() => shareDocument("telegram")} data-testid="invoice-share-telegram-button">
              <Send size={15} className="mr-1" /> Telegram
            </Button>
            {(doc.type === "sale" || doc.type === "estimate" || doc.type === "credit_note") && (
              <Button
                variant="outline"
                size="sm"
                onClick={emailDoc}
                disabled={emailing || invoiceEmailEnabled !== true}
                title={invoiceEmailEnabled === false ? "Emailing invoices is not included in your plan." : undefined}
                data-testid="invoice-email-button"
              >
                <EnvelopeSimple size={15} className="mr-1" /> {emailing ? "Sending…" : "Email"}
              </Button>
            )}
            <Button variant="destructive" size="sm" onClick={del} data-testid="invoice-delete-button">Delete</Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Download the PDF first, then attach it in WhatsApp or Telegram before sending.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="invoice-detail-close-button">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
    <Dialog open={editOpen} onOpenChange={setEditOpen}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="invoice-edit-details-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">{doc.type === "estimate" ? "Edit Estimate" : "Edit Invoice Details"} · {doc.number}</DialogTitle>
          <DialogDescription>Update billing and delivery addresses, transport details, and optional terms.</DialogDescription>
        </DialogHeader>
        <section className="space-y-3">
          <h3 className="text-sm font-semibold">Bill To · {doc.party_name}</h3>
          <div className="space-y-2">
            <Label htmlFor="edit-party-address">Address line</Label>
            <Input id="edit-party-address" value={editForm.party_address} onChange={(e) => setEditForm((f) => ({ ...f, party_address: e.target.value }))} data-testid="edit-invoice-bill-address-input" />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="edit-party-district">District</Label>
              <Input id="edit-party-district" value={editForm.party_district} onChange={(e) => setEditForm((f) => ({ ...f, party_district: e.target.value }))} data-testid="edit-invoice-bill-district-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-party-state">State</Label>
              <Input id="edit-party-state" value={editForm.party_state} onChange={(e) => setEditForm((f) => ({ ...f, party_state: e.target.value }))} data-testid="edit-invoice-bill-state-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-party-pin">PIN code</Label>
              <Input id="edit-party-pin" inputMode="numeric" maxLength={6} value={editForm.party_pin_code} onChange={(e) => setEditForm((f) => ({ ...f, party_pin_code: e.target.value }))} data-testid="edit-invoice-bill-pin-input" />
            </div>
          </div>
        </section>
        <div className="flex items-center gap-2">
          <Checkbox
            id="edit-invoice-different-shipping"
            checked={editForm.ship_to_different}
            onCheckedChange={(checked) => setEditForm((f) => ({ ...f, ship_to_different: checked === true }))}
            data-testid="edit-invoice-different-shipping-checkbox"
          />
          <Label htmlFor="edit-invoice-different-shipping">Ship to a different address</Label>
        </div>
        {editForm.ship_to_different && (
          <section className="space-y-3 rounded-md border p-3" data-testid="edit-invoice-ship-to">
            <h3 className="text-sm font-semibold">Ship To</h3>
            <div className="space-y-2">
              <Label htmlFor="edit-shipping-address">Address line *</Label>
              <Input id="edit-shipping-address" value={editForm.shipping_address} onChange={(e) => setEditForm((f) => ({ ...f, shipping_address: e.target.value }))} data-testid="edit-invoice-shipping-address-input" />
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="edit-shipping-district">District</Label>
                <Input id="edit-shipping-district" value={editForm.shipping_district} onChange={(e) => setEditForm((f) => ({ ...f, shipping_district: e.target.value }))} data-testid="edit-invoice-shipping-district-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-shipping-state">State</Label>
                <Input id="edit-shipping-state" value={editForm.shipping_state} onChange={(e) => setEditForm((f) => ({ ...f, shipping_state: e.target.value }))} data-testid="edit-invoice-shipping-state-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-shipping-pin">PIN code</Label>
                <Input id="edit-shipping-pin" inputMode="numeric" maxLength={6} value={editForm.shipping_pin_code} onChange={(e) => setEditForm((f) => ({ ...f, shipping_pin_code: e.target.value }))} data-testid="edit-invoice-shipping-pin-input" />
              </div>
            </div>
          </section>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="edit-invoice-eway">E-Way Bill No.</Label>
            <Input id="edit-invoice-eway" maxLength={30} value={editForm.eway_bill_no} onChange={(e) => setEditForm((f) => ({ ...f, eway_bill_no: e.target.value }))} data-testid="edit-invoice-eway-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-invoice-vehicle">Vehicle No.</Label>
            <Input id="edit-invoice-vehicle" maxLength={20} value={editForm.vehicle_no} onChange={(e) => setEditForm((f) => ({ ...f, vehicle_no: e.target.value }))} data-testid="edit-invoice-vehicle-input" />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="edit-invoice-terms">Terms and Conditions (optional)</Label>
          <Textarea id="edit-invoice-terms" rows={4} maxLength={4000} value={editForm.terms_and_conditions} onChange={(e) => setEditForm((f) => ({ ...f, terms_and_conditions: e.target.value }))} data-testid="edit-invoice-terms-input" />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setEditOpen(false)} data-testid="edit-invoice-cancel-button">Cancel</Button>
          <Button onClick={saveInvoiceDetails} disabled={savingDetails} data-testid="edit-invoice-save-button">{savingDetails ? "Saving…" : doc.type === "estimate" ? "Update Estimate" : "Update Invoice"}</Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
